import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertController, AlertInput, IonButton, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonContent, IonIcon, IonModal, ModalController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { AppService } from 'src/app/services/app.service';
import { LoadingService } from 'src/app/services/loading.service';
import { MeService } from 'src/app/services/me.service';
import { addIcons } from 'ionicons';
import { pricetagOutline, listOutline, filter, todayOutline } from 'ionicons/icons';
import { Subscription, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { UserProfile } from 'src/app/models/user-profile.model';
import { CampusEvent } from 'src/app/models/campus.model';
import { EventService } from 'src/app/services/event.service';
import { campusId } from 'src/app/constants';

@Component({
  selector: 'app-local-events',
  templateUrl: './local-events.page.html',
  styleUrls: ['./local-events.page.scss'],
  standalone: true,
  imports: [
        IonModal,
        IonIcon,
        IonButton,
        IonCardContent,
        IonCardSubtitle,
        IonCardTitle,
        IonCardHeader,
        IonCard,
        IonContent,
        CommonModule,
        FormsModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LocalEventsPage {
  currentSort: WritableSignal<string> = signal('CreatedDate%20desc');
  currentFilter: WritableSignal<string> = signal('All');
  queryParams = new Map<string, any>();
  events: CampusEvent[] = [];
  categories: any[] = []

  image: WritableSignal<string | undefined> = signal(undefined);
  backSubscription?: Subscription;
  user!: UserProfile;

  sortOptions: Signal<AlertInput[]> = computed(() => [
    {
      label: 'Newest First',
      type: 'radio',
      value: 'CreatedDate%20desc',
      checked: this.currentSort() == 'CreatedDate%20desc',
    },
    {
      label: 'Oldest First',
      type: 'radio',
      value: 'CreatedDate%20asc',
      checked: this.currentSort() == 'CreatedDate%20asc',
    },
    {
      label: 'Alphabetical (A to Z)',
      type: 'radio',
      value: 'Event.Title%20asc',
      checked: this.currentSort() == 'Event.Title%20asc',
    },
    {
      label: 'Alphabetical (Z to A)',
      type: 'radio',
      value: 'Event.Title%20desc',
      checked: this.currentSort() == 'Event.Title%20desc',
    },
  ]);

  private searchSubject$ = new Subject<string>();

  public sortButtons = [
    {
      text: 'Apply',
      role: 'confirm',
      handler: (data: string) => {
        this.currentSort.set(data);
        this.queryParams.set('OrderBy', data);

        this.getEvents();
      },
    },
  ];

  constructor(
    private eventService: EventService,
    private router: Router,
    private appService: AppService,
    private meService: MeService,
    private alertController: AlertController,
    private loadingService: LoadingService,
  ) {
    addIcons({ filter, listOutline, pricetagOutline, todayOutline });
  }

  ionViewWillEnter(): void {
    this.queryParams.set('Status', 'ACTIVE');
    this.backSubscription = this.appService.backSubject.subscribe((_) => {
      this.router.navigateByUrl('/home');
    });

    this.meService.getUserProfile().subscribe({
      next: (response) => {
        this.user = response;
        this.queryParams.set(campusId, response.campusId);
      },
      error: () => {},
    });
    this.queryParams.set('OrderBy', 'CreatedDate%20desc');
    this.getEvents();

    this.searchSubject$
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((text) => {
        if (text && text.trim().length > 0) {
          this.queryParams.set('Title', text);
        }

        if (text.trim().length == 0) {
          this.queryParams.delete('Title');
        }
        this.getEvents();
      });
  }

  getEvents() {
    this.loadingService.showLoading('Getting the latest events!');

    this.meService.getUserProfile().subscribe({
      next: (response) => {
        this.queryParams.set(campusId, response.campusId);
        this.eventService
          .getCampusEvents(this.queryParams)
          .subscribe((response) => {
            this.events = response;
            this.loadingService.hideLoading();
          });
      },

      error: () => {
        this.loadingService.hideLoading();
      },
    });
  }

  ionViewWillLeave(): void {
    if (this.backSubscription) {
      this.backSubscription.unsubscribe();
    }

    if (this.searchSubject$) {
      this.searchSubject$.complete();
    }
  }

  goToDetails(id: string | undefined) {
    if (id) {
      this.router.navigateByUrl(`/local-events/${id}`);
    }
  }

  sendToLink(url: string) {
    if (url != '' && url != null) {
      window.open(url);
    }
  }

  openModal(item?: CampusEvent) {
    this.image.set(item?.event.image?.[0]?.url);
  }

  closeModal() {
    this.image.set(undefined);
  }

  filterEvents(event: CustomEvent) {
    const textValue = event.detail.value;
    this.searchSubject$.next(textValue);
  }

  async eventSort() {
    const alert = await this.alertController.create({
      header: 'Product Sort',
      subHeader: 'Select Sort Criteria Below',
      buttons: this.sortButtons,
      inputs: this.sortOptions(),
      cssClass: 'themed-alert',
    });

    await alert.present();
  }

  visitSite(url: string) {
    if (url && url != '') {
      window.open(url, '_system');
    }
  }
}
